import React, { useEffect, useRef, useState } from "react";
import Modal from "../../ui/modal/Modal";
import { ShippingUnitDetails } from "../../../types/orders";
import { ordersService } from "../../../api/ordersService/ordersService";
import { Table } from "react-bootstrap";

type Props = {
    callbackCloseModal: () => any;
    orderId: number | null;
    orderNumber: string;
    isGroupedOrdersModal?: boolean;
};

const ModalShowShipmentUnits: React.FC<Props> = ({
    callbackCloseModal,
    orderId,
    orderNumber,
    isGroupedOrdersModal = false
}) => {
    const [data, setData] = useState<null | ShippingUnitDetails[]>([]);
    const [loading, setLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState<{ key?: keyof ShippingUnitDetails; direction: "ascending" | "descending" }>({ direction: "ascending" });

    const canGo = useRef(true);
    useEffect(() => {
        if (canGo.current === true && orderId !== null) {
            canGo.current = false;
            handleGetSetShippingUnitDetails();
        }
    }, []);

    const handleGetSetShippingUnitDetails = async () => {
        if (orderId !== null) {
            setLoading(true);
            const response = await ordersService.getShipmentUnits(orderId, isGroupedOrdersModal);
            if (response.success) {
                setData(response.data);
            }
            setLoading(false);
        }
    };

    const handleSort = (key: keyof ShippingUnitDetails) => {
        let direction: "ascending" | "descending" = "ascending";
        if (sortConfig.key === key && sortConfig.direction === "ascending") {
            direction = "descending";
        }
        setSortConfig({ key, direction });
    };

    const sortedData = React.useMemo(() => {
        if (!data || data.length === 0 || !sortConfig.key) return data;
        return [...data].sort((a, b) => {
            if (sortConfig.key && a[sortConfig.key]! < b[sortConfig.key]!) {
                return sortConfig.direction === "ascending" ? -1 : 1;
            }
            if (sortConfig.key && a[sortConfig.key]! > b[sortConfig.key]!) {
                return sortConfig.direction === "ascending" ? 1 : -1;
            }
            return 0;
        });
    }, [data, sortConfig]);

    return (
        <Modal
            callbackCloseModal={callbackCloseModal}
            title={"Shipment Units - Order " + orderNumber}
            customStyleChildrenContainer={{ overflow: "auto" }}
            customStyleContainer={{ maxHeight: "75%", width: "80%", maxWidth: "1920px" }}
        >
            <>
                {loading && <div>Loading...</div>}

                {!loading && (
                    <>
                        {data && data.length > 0 ? (
                            <div className="overflow-hidden">
                                <Table>
                                    <thead className="bg-grey">
                                        <tr>
                                            {["quantity", "package", "stackability", "width", "length", "height"].map((key) => (
                                                <th key={key} className="p-2 cursor-pointer" onClick={() => handleSort(key as keyof ShippingUnitDetails)}>
                                                    <span className="whitespace-nowrap w-65px block">
                                                        {`${key.charAt(0).toUpperCase() + key.slice(1)}${sortConfig.key === key ? (sortConfig.direction === "ascending" ? " ▲" : " ▼") : ""}`}
                                                    </span>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white">
                                        {sortedData?.map((d, index) => (
                                            <tr key={index}>
                                                <td className="p-2">{d.quantity}</td>
                                                <td className="p-2">{d.package}</td>
                                                <td className="p-2">{d.stackability}</td>
                                                <td className="p-2">{d.width}</td>
                                                <td className="p-2">{d.length}</td>
                                                <td className="p-2">{d.height}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <div>There are no Shipment Units to show.</div>
                        )}
                    </>
                )}
            </>
        </Modal>
    );
};

export default ModalShowShipmentUnits;
